"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import DropdownSelectOne from "../../../components/custom/dropdown-select-one";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import { ApiConfig, API_USER_ROUTER } from "../../../services/api-config";
import { useDebounce } from "../../../hooks/use-debounce";
import { useStoreOptions } from "../../../hooks/use-store-options";
import FaultTicketService, {
  FaultTicket,
  FaultTicketListResponse,
  FAULT_SEVERITY_LABEL,
  FAULT_STATUS,
  FAULT_STATUS_LABEL,
  FAULT_SYNC_STATUS,
  FAULT_SYNC_STATUS_CLASS,
  FAULT_SYNC_STATUS_LABEL,
  FAULT_TYPE_LABEL,
  formatDateTime,
  SyncProgress,
  SyncStartResult,
} from "../services/fault-ticket-service";
import FaultTicketCreateModal from "../components/fault-ticket-create-modal";
import FaultTicketDetailModal from "../components/fault-ticket-detail-modal";
import EmptyState from "../../../components/custom/empty-state";

const STATUS_BADGE_STYLES: Record<number, string> = {
  [FAULT_STATUS.OPEN]: "bg-amber-100 text-amber-700",
  [FAULT_STATUS.ACKNOWLEDGED]: "bg-blue-100 text-blue-700",
  [FAULT_STATUS.RESOLVED]: "bg-green-100 text-green-700",
  [FAULT_STATUS.REJECTED]: "bg-slate-200 text-slate-600",
};

/** Nhịp hỏi tiến độ đồng bộ. Phiên chạy hàng phút nên 3s là đủ mượt mà không dội request. */
const SYNC_POLL_MS = 3000;
/** Trần chờ ở phía web; BE cũng tự dừng phiên ở 10 phút. Vượt thì thôi theo dõi, không huỷ phiên. */
const SYNC_MAX_WAIT_MS = 12 * 60 * 1000;

interface EmployeeOption {
  id: number;
  name?: string;
}

// Khai báo ngoài component để giữ nguyên tham chiếu giữa các lần render (select dùng làm dependency).
// KHÔNG lọc theo chi nhánh/trạng thái: NV chịu trách nhiệm có thể thuộc chi nhánh khác chi nhánh của phiếu.
const ALL_EMPLOYEES_OPTION: EmployeeOption = { id: 0, name: "Tất cả nhân viên" };
const EMPLOYEE_FILTER_PARAMS = {};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** "Đã gửi 12 phiếu · 2 lỗi · 1 cần kiểm tra" */
const syncSummary = (p: SyncProgress): string => {
  const parts = [`Đã gửi ${p.sent} phiếu`];
  if (p.failed) parts.push(`${p.failed} lỗi`);
  if (p.needsCheck) parts.push(`${p.needsCheck} cần kiểm tra`);
  if (p.error) parts.push(p.error);
  return parts.join(" · ");
};

// dd/MM/yyyy (khớp DateInputCustom + BE TimeHelper)
const toDMY = (d: Date) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

const FaultTicketIndexPage: React.FC = () => {
  const { fetch: fetchList } = FaultTicketService.list();
  const { fetch: syncRun } = FaultTicketService.syncRun();
  const { fetch: syncStatus } = FaultTicketService.syncStatus();
  const { fetch: syncRetry } = FaultTicketService.syncRetry();
  const { options: branchOptions } = useStoreOptions();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState<FaultTicketListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<number | "all">("all");
  const [branchId, setBranchId] = useState<number | "">("");
  const [faultType, setFaultType] = useState<number>(0); // 0 = tất cả loại lỗi
  const [responsibleUserId, setResponsibleUserId] = useState<number>(0);
  const [fromDate, setFromDate] = useState<string>(toDMY(firstDayOfMonth));
  const [toDate, setToDate] = useState<string>(toDMY(today));
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 800);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState<FaultTicket | null>(null);
  const [syncing, setSyncing] = useState(false);
  /** Dòng tiến độ hiện trên nút, vd "Đang gửi 30/120..." */
  const [syncNote, setSyncNote] = useState("");
  /** Rời trang giữa chừng thì ngừng poll (phiên ở BE vẫn chạy tiếp bình thường). */
  const leftPageRef = useRef(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      leftPageRef.current = true;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchList({
        status: status === "all" ? undefined : Number(status),
        branch_id: branchId ? Number(branchId) : undefined,
        fault_type: faultType || undefined,
        responsible_user_id: responsibleUserId || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        key_search: debouncedSearch || undefined,
        page,
        limit,
      });
      if (res?.data) setData(res.data as FaultTicketListResponse);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, branchId, faultType, responsibleUserId, fromDate, toDate, debouncedSearch, page, limit]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setPage(1);
  }, [status, branchId, faultType, responsibleUserId, fromDate, toDate, debouncedSearch, limit]);

  const totalRecords = data?.total_record ?? 0;
  const rows = data?.list ?? [];
  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));

  // ===== Đồng bộ sang hệ thống đối tác =====
  /**
   * BE chạy phiên đồng bộ ở NỀN (mỗi lô chờ đối tác tới 20s, làm đồng bộ thì nginx cắt 504),
   * nên ở đây bấm xong phải poll `status` cho tới khi `running=false` mới có kết quả để báo.
   */
  const handleSyncAll = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncNote("");
    try {
      const res = await syncRun();
      const start = res?.data as SyncStartResult | undefined;
      if (!start) return; // lỗi cấu hình / mạng: đã có thông báo lỗi chung của useFetchData

      // started=false nghĩa là người khác (hoặc lần bấm trước) đang chạy — bám theo phiên đó,
      // KHÔNG khởi động phiên thứ hai, tránh gửi trùng làm nhân viên bị phạt hai lần.
      let last: SyncProgress = start;
      const deadline = Date.now() + SYNC_MAX_WAIT_MS;

      while (last.running && Date.now() < deadline && !leftPageRef.current) {
        setSyncNote(`Đang gửi ${last.processed}/${last.total || "?"}...`);
        await sleep(SYNC_POLL_MS);
        const statusRes = await syncStatus();
        const current = statusRes?.data as SyncProgress | undefined;
        if (!current) break; // mất kết nối: bỏ theo dõi, phiên ở BE vẫn chạy tiếp
        last = current;
      }

      if (leftPageRef.current) return;

      alert(
        last.running
          ? `Phiên đồng bộ vẫn đang chạy. ${syncSummary(last)}. Mở lại trang để xem kết quả.`
          : syncSummary(last),
      );
      await load();
    } finally {
      if (!leftPageRef.current) {
        setSyncing(false);
        setSyncNote("");
      }
    }
  };

  const handleRetry = async (ticket: FaultTicket) => {
    // Phiếu "cần kiểm tra" = không rõ đối tác đã ghi hay chưa. Bắt xác nhận để không phạt trùng.
    if (ticket.sync_status === FAULT_SYNC_STATUS.NEEDS_CHECK) {
      const ok = window.confirm(
        `Phiếu ${ticket.code} đã gửi đi nhưng không rõ đối tác có ghi nhận hay không.\n\n` +
          "Chỉ gửi lại khi đã xác nhận với đối tác là bản ghi trước CHƯA vào, nếu không nhân viên sẽ bị phạt hai lần.\n\nTiếp tục gửi lại?",
      );
      if (!ok) return;
    }
    setRetryingId(ticket.id);
    try {
      await syncRetry(ticket.id);
      await load();
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Quản lý phiếu lỗi</h1>
          <p className="text-sm text-slate-500 mt-1">Theo dõi và xử lý phiếu lỗi nhân sự</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSyncAll}
            disabled={syncing}
            title="Gửi các phiếu đã ghi lỗi chưa đồng bộ trong 10 ngày gần nhất sang hệ thống đối tác"
            className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-semibold whitespace-nowrap disabled:opacity-50"
          >
            {syncing ? syncNote || "Đang đồng bộ..." : "Đồng bộ sang đối tác"}
          </button>
          <button
            onClick={() => setOpenCreate(true)}
            className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold whitespace-nowrap"
          >
            + Tạo phiếu lỗi
          </button>
        </div>
      </div>

      {/* Filter Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Hàng 1: Trạng thái + Tìm kiếm */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 p-4">
          <div className="min-w-[200px]">
            <DropdownSelectOne
              defaultOption={{ value: "all", label: "Tất cả trạng thái" }}
              options={Object.entries(FAULT_STATUS_LABEL).map(([value, label]) => ({
                value: Number(value),
                label,
              }))}
              selectedId={status === "all" ? "all" : status}
              onChange={(opt) => setStatus(opt.value === "all" ? "all" : Number(opt.value))}
              getOptionLabel={(o) => o.label}
              getOptionKey={(o) => o.value}
              placeholder="Trạng thái"
            />
          </div>

          {/* Lọc theo khoảng thời gian (lịch tiếng Việt) */}
          <div className="flex items-center gap-2">
            <DateInputCustom
              type="date"
              value={fromDate}
              onChange={(d) => setFromDate(d ?? "")}
              placeholder="Từ ngày"
              className="w-[150px]"
            />
            <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            <DateInputCustom
              type="date"
              value={toDate}
              onChange={(d) => setToDate(d ?? "")}
              placeholder="Đến ngày"
              className="w-[150px]"
            />
          </div>

          <div className="flex-1 relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              // Gõ mã đơn ("OD76" hoặc "76") → BE tìm thêm theo đơn liên quan.
              placeholder="Tìm mã phiếu, mã đơn, mô tả..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Hàng 2: Lọc theo chi nhánh + nhân viên chịu trách nhiệm */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 p-4 bg-slate-50 border-t border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Lọc theo
          </span>
          <div className="min-w-[200px]">
            <DropdownSelectOne
              defaultOption={{ value: 0, label: "Tất cả chi nhánh" }}
              options={branchOptions}
              selectedId={branchId === "" ? 0 : Number(branchId)}
              onChange={(opt) => setBranchId(opt.value === 0 ? "" : Number(opt.value))}
              getOptionLabel={(o) => o.label}
              getOptionKey={(o) => o.value}
              placeholder="Chi nhánh"
              isSearchLocal
            />
          </div>
          <div className="min-w-[220px]">
            <DropdownSelectOne
              defaultOption={{ value: 0, label: "Tất cả loại lỗi" }}
              options={Object.entries(FAULT_TYPE_LABEL).map(([value, label]) => ({
                value: Number(value),
                label,
              }))}
              selectedId={faultType}
              onChange={(opt) => setFaultType(Number(opt.value) || 0)}
              getOptionLabel={(o) => o.label}
              getOptionKey={(o) => o.value}
              placeholder="Loại lỗi"
              isSearchLocal
            />
          </div>
          {/* Tìm theo tên ngay trong ô chọn (gọi API, phân trang) — danh sách nhân viên có thể dài */}
          <SelectDropdownAsyncPaginate<EmployeeOption>
            url={`${API_USER_ROUTER.API_LIST}`}
            projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
            placeholder="NV chịu trách nhiệm"
            isMulti={false}
            allOptions={ALL_EMPLOYEES_OPTION}
            onChange={(selected) => setResponsibleUserId(Number(selected[0]?.id) || 0)}
            labelOption="name"
            classNames="min-w-[260px]"
            params={EMPLOYEE_FILTER_PARAMS}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto overflow-y-auto max-h-[65vh] w-full scroll-smooth">
        <table className="min-w-[1100px] w-full text-sm text-slate-700 border-collapse">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0 z-10 text-center whitespace-nowrap">
            <tr>
              <th className="px-4 py-3 text-center border border-slate-200">STT</th>
              <th className="px-4 py-3 text-center border border-slate-200">Mã phiếu</th>
              <th className="px-4 py-3 text-center border border-slate-200">Chi nhánh</th>
              <th className="px-4 py-3 text-center border border-slate-200">Loại lỗi</th>
              <th className="px-4 py-3 text-center border border-slate-200">Mức độ</th>
              <th className="px-4 py-3 text-center border border-slate-200">NV chịu TN</th>
              <th className="px-4 py-3 text-center border border-slate-200">Đơn liên quan</th>
              <th className="px-4 py-3 text-center border border-slate-200">Điểm phạt</th>
              <th className="px-4 py-3 text-center border border-slate-200">Tiền phạt</th>
              <th className="px-4 py-3 text-center border border-slate-200">Trạng thái</th>
              <th className="px-4 py-3 text-center border border-slate-200">Đồng bộ</th>
              <th className="px-4 py-3 text-center border border-slate-200">Ngày tạo</th>
              <th className="px-4 py-3 text-center sticky right-0 bg-slate-100 z-30 shadow-md border border-slate-200">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={13} className="py-6 text-center text-slate-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={13}>
                  <EmptyState
                    message="Chưa có phiếu lỗi"
                    description={
                      status !== "all" || branchId || faultType || responsibleUserId || debouncedSearch
                        ? "Không tìm thấy phiếu lỗi khớp bộ lọc."
                        : "Không có phiếu lỗi nào trong khoảng thời gian đã chọn."
                    }
                  />
                </td>
              </tr>
            ) : (
              rows.map((t, idx) => (
                <tr key={t.id} className="border-t hover:bg-slate-50 transition group">
                  <td className="px-4 py-3 border border-slate-200 text-center">
                    {(page - 1) * limit + idx + 1}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800 border border-slate-200 text-center">
                    {t.code}
                  </td>
                  <td className="px-4 py-3 border border-slate-200">
                    {t.branch_name || `#${t.branch_id}`}
                  </td>
                  <td className="px-4 py-3 border border-slate-200 text-center">
                    <div>{FAULT_TYPE_LABEL[t.fault_type] || t.fault_type}</div>
                    {t.milestone_name && (
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {t.milestone_name}
                        {t.escalation_level ? ` · Lần ${t.escalation_level}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 border border-slate-200 text-center">
                    {FAULT_SEVERITY_LABEL[t.severity] || t.severity}
                  </td>
                  <td className="px-4 py-3 border border-slate-200">
                    {t.responsible_name || `NV #${t.responsible_user_id}`}
                  </td>
                  <td className="px-4 py-3 border border-slate-200 text-center">
                    {t.order_code || (t.order_id ? `#${t.order_id}` : "—")}
                  </td>
                  <td className="px-4 py-3 border border-slate-200 text-center">
                    {t.penalty_points != null ? `${t.penalty_points} điểm` : "—"}
                  </td>
                  <td className="px-4 py-3 border border-slate-200 text-right">
                    {t.penalty_amount != null
                      ? Number(t.penalty_amount).toLocaleString("vi-VN")
                      : "—"}
                  </td>
                  <td className="px-4 py-4 text-center border border-slate-200">
                    <span
                      className={`px-2 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap min-w-[110px] inline-block text-center ${
                        STATUS_BADGE_STYLES[t.status] || "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {FAULT_STATUS_LABEL[t.status] || "Không rõ"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center border border-slate-200">
                    {/* Chỉ phiếu ĐÃ CHỐT mới nằm trong diện đồng bộ — phiếu khác để trống cho đỡ nhiễu */}
                    {t.status !== FAULT_STATUS.RESOLVED ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <span
                          title={t.sync_error || undefined}
                          className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                            FAULT_SYNC_STATUS_CLASS[t.sync_status ?? 0] || "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {FAULT_SYNC_STATUS_LABEL[t.sync_status ?? 0] || "Chưa gửi"}
                        </span>
                        {t.sync_status === FAULT_SYNC_STATUS.SENT && t.synced_at && (
                          <span className="text-[11px] text-slate-400">
                            {formatDateTime(t.synced_at)}
                          </span>
                        )}
                        {(t.sync_status === FAULT_SYNC_STATUS.FAILED ||
                          t.sync_status === FAULT_SYNC_STATUS.NEEDS_CHECK) && (
                          <button
                            onClick={() => handleRetry(t)}
                            disabled={retryingId === t.id}
                            className="text-[11px] text-blue-600 hover:underline disabled:opacity-50"
                          >
                            {retryingId === t.id ? "Đang gửi..." : "Gửi lại"}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center border border-slate-200">
                    {formatDateTime(t.created_at)}
                  </td>
                  <td className="px-4 py-3 text-center sticky right-0 bg-white group-hover:bg-slate-50 transition-colors border border-slate-200">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => setSelected(t)}
                        title="Xem chi tiết"
                        className="h-8 w-8 flex items-center justify-center rounded-full border border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-all"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="h-5 w-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z"
                          />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          {totalRecords > 0
            ? `Hiển thị ${(page - 1) * limit + 1} đến ${(page - 1) * limit + rows.length} của ${totalRecords} hàng`
            : "Không có dữ liệu"}
        </div>
        <div className="flex items-center gap-3">
          <select
            className="border rounded px-2 py-1"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          {(() => {
            const pages: (number | string)[] = [];
            const add = (p: number | string) => pages.push(p);
            const start = Math.max(1, page - 1);
            const end = Math.min(totalPages, page + 1);
            add(1);
            if (start > 2) add("...");
            for (let p = start; p <= end; p++) if (p !== 1 && p !== totalPages) add(p);
            if (end < totalPages - 1) add("...");
            if (totalPages > 1) add(totalPages);
            return (
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {"<"}
                </button>
                {pages.map((p, idx) =>
                  typeof p === "number" ? (
                    <button
                      key={idx}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded-full ${
                        p === page
                          ? "bg-green-600 text-white"
                          : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                      }`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={idx} className="px-2">
                      {p}
                    </span>
                  )
                )}
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {">"}
                </button>
              </div>
            );
          })()}
        </div>
      </div>

      <FaultTicketCreateModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => {
          setPage(1);
          load();
        }}
      />
      <FaultTicketDetailModal
        open={!!selected}
        ticket={selected}
        onClose={() => setSelected(null)}
        onUpdated={() => load()}
      />
    </div>
  );
};

export default FaultTicketIndexPage;
